import { useState, useEffect } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Loader, X } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Renders ONE chart for a single selected parameter, with real data fetched
 * from /api/data/graph-data/. Title is "<Site Name> | <Parameter> (<unit>)".
 *
 * IMPORTANT: `data` and the `period` it was built for are stored together in a
 * single state object. The chart axis type/formatting is derived from that
 * stored period — never from the live `timePeriod` prop. This prevents a render
 * where the new axis type (e.g. numeric) sees old-format data (month-key
 * strings), which made recharts throw "DecimalError: Invalid argument: NaN".
 */
// Custom dot renderer for non-circle point shapes
function makeDot(shape, color) {
  if (shape === 'None') return false
  if (!shape || shape === 'Circle') return { r: 2, fill: color }
  return (props) => {
    const { cx, cy } = props
    if (cx == null || cy == null) return null
    const s = 4
    if (shape === 'Square')   return <rect x={cx - s} y={cy - s} width={s * 2} height={s * 2} fill={color} />
    if (shape === 'Diamond')  return <path d={`M${cx},${cy - s} L${cx + s},${cy} L${cx},${cy + s} L${cx - s},${cy} Z`} fill={color} />
    if (shape === 'Triangle') return <path d={`M${cx},${cy - s} L${cx + s},${cy + s} L${cx - s},${cy + s} Z`} fill={color} />
    return <circle cx={cx} cy={cy} r={2} fill={color} />
  }
}

export function SingleParameterChart({ param, dateRange = {}, timezone = 'UTC', timePeriod = 'Y', color = '#358ECD', pointShape = 'Circle', yMin, yMax, ySteps, viewMode = 'chart', onRemove, onData, embedded = false }) {
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  // data + the period it represents, always consistent
  const [chart, setChart]     = useState({ data: [], period: timePeriod })
  const [meta, setMeta]       = useState({ unit: param.unit || '', latestValue: null })

  const toNum = (v) => {
    if (v === null || v === undefined || v === '') return null
    const n = typeof v === 'string' ? parseFloat(v) : v
    return (typeof n === 'number' && !isNaN(n)) ? n : null
  }

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      if (!param?.id) return
      setLoading(true)
      setError(null)
      try {
        const response = await projectsApi.fetchGraphData(Number(param.id), {
          fromDate: dateRange.from,
          toDate:   dateRange.to,
          timezone,
        })
        if (cancelled) return

        if (!response || typeof response === 'string' || response.error === true || !Array.isArray(response.data)) {
          setChart({ data: [], period: timePeriod })
          setMeta({ unit: param.unit || '', latestValue: null })
          if (response?.error) setError(response.message || 'Failed to load data')
          return
        }

        // Build {x:number, y:number|null} points — both coerced, x always valid
        const points = []
        response.data.forEach((p) => {
          const xNum = typeof p.x === 'string' ? parseInt(p.x, 10) : p.x
          if (xNum === null || xNum === undefined || isNaN(xNum)) return
          points.push({ x: xNum, y: toNum(p.y) })
        })

        let processed
        if (timePeriod === 'Y') {
          // Group by month; X axis becomes category month-keys
          const grouped = {}
          points.forEach((pt) => {
            const d = new Date(pt.x)
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            grouped[key] = { x: key, y: pt.y, label: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}` }
          })
          if (dateRange.from && dateRange.to) {
            const out = []
            const from = new Date(dateRange.from)
            const to   = new Date(dateRange.to)
            let y = from.getFullYear(), m = from.getMonth() + 1
            const endY = to.getFullYear(), endM = to.getMonth() + 1
            let guard = 0
            while ((y < endY || (y === endY && m <= endM)) && guard < 240) {
              const key = `${y}-${String(m).padStart(2, '0')}`
              out.push(grouped[key] ? grouped[key] : { x: key, y: null, label: `${MONTH_NAMES[m - 1]} ${String(y).slice(-2)}` })
              m++; if (m > 12) { m = 1; y++ }
              guard++
            }
            processed = out
          } else {
            processed = Object.values(grouped).sort((a, b) => a.x.localeCompare(b.x))
          }
        } else {
          processed = points
            .filter((pt) => pt.y !== null)
            .sort((a, b) => a.x - b.x)
        }

        // If there isn't a single numeric Y value, render as "no data"
        // (prevents recharts axis-domain NaN computation).
        const hasNumericY = processed.some((pt) => typeof pt.y === 'number' && !isNaN(pt.y))
        const finalData = hasNumericY ? processed : []
        setChart({ data: finalData, period: timePeriod })
        const u = response.unit || param.unit || ''
        setMeta({ unit: u, latestValue: response.latestValue ?? null })
        // Report data upward so the parent can build a combined CSV/export
        onData?.(param.id, { param, unit: u, period: timePeriod, points: finalData })
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load data')
          setChart({ data: [], period: timePeriod })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [param?.id, dateRange.from, dateRange.to, timezone, timePeriod])

  const unit = meta.unit || param.unit || ''
  const title = `${param.siteName || param.deviceName || 'Site'} | ${param.name}${unit ? ` (${unit})` : ''}`

  // Derive everything from the period the data was actually built for
  const renderPeriod = chart.period
  const data = chart.data
  const xIsCategory = renderPeriod === 'Y'

  const formatX = (value) => {
    if (value === null || value === undefined) return ''
    if (xIsCategory && typeof value === 'string' && value.includes('-')) {
      const [year, month] = value.split('-')
      return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year.slice(-2)}`
    }
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    switch (renderPeriod) {
      case 'M': return d.toLocaleString('en-US', { day: '2-digit' })
      case 'W': return d.toLocaleString('en-US', { weekday: 'short', day: '2-digit' })
      case 'D': return d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
      default:  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const v = payload[0].value
    return (
      <div className="bg-white p-2 border border-gray-300 rounded shadow text-xs">
        <p className="font-semibold text-gray-700">{formatX(label)}</p>
        <p style={{ color }}>{param.name}: {typeof v === 'number' ? v.toFixed(2) : v} {unit}</p>
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'bg-white rounded shadow-sm p-4'}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#4D4D4D] text-base">{title}</h3>
        {onRemove && (
          <button
            onClick={onRemove}
            title="Remove this parameter from comparison"
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded transition"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-[200px] flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" /> Loading data…
        </div>
      ) : error ? (
        <div className="h-[200px] flex items-center justify-center text-red-500 text-sm">{error}</div>
      ) : data.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
          No data available for the selected time period
        </div>
      ) : viewMode === 'table' ? (
        <DataTable data={data} unit={unit} formatX={formatX} />
      ) : viewMode === 'stats' ? (
        <StatsView data={data} unit={unit} />
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          {/* key forces a clean remount when the axis type changes */}
          <LineChart key={renderPeriod} data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="x"
              type={xIsCategory ? 'category' : 'number'}
              domain={xIsCategory ? undefined : ['dataMin', 'dataMax']}
              tickFormatter={formatX}
              tick={{ fontSize: 11, fill: '#888' }}
              interval="preserveStartEnd"
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#888' }}
              width={40}
              allowDecimals
              domain={[
                (yMin !== undefined && yMin !== null && yMin !== '') ? Number(yMin) : 'auto',
                (yMax !== undefined && yMax !== null && yMax !== '') ? Number(yMax) : 'auto',
              ]}
              tickCount={(ySteps !== undefined && ySteps !== null && ySteps !== '') ? Number(ySteps) : undefined}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="y"
              stroke={color}
              strokeWidth={2}
              dot={makeDot(pointShape, color)}
              isAnimationActive={false}
              connectNulls={true}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {viewMode === 'chart' && meta.latestValue !== null && meta.latestValue !== undefined && (
        <div className="text-xs text-center mt-2 text-gray-600">
          {param.deviceName ? `${param.deviceName} : ` : ''}{param.name}{unit ? ` (${unit})` : ''} - {meta.latestValue} {unit}
        </div>
      )}
    </div>
  )
}

// ─── Data table view ──────────────────────────────────────────────────────────
function DataTable({ data, unit, formatX }) {
  const rows = data.filter(d => d.y !== null && d.y !== undefined)
  return (
    <div className="max-h-[240px] overflow-y-auto border border-gray-100 rounded">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Time</th>
            <th className="text-right px-3 py-2 font-semibold text-gray-600">Value{unit ? ` (${unit})` : ''}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="px-3 py-1.5 text-gray-700">{formatX(d.x)}</td>
              <td className="px-3 py-1.5 text-right text-gray-900 font-medium">{typeof d.y === 'number' ? d.y.toFixed(2) : d.y}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Statistics view ──────────────────────────────────────────────────────────
function StatsView({ data, unit }) {
  const ys = data.map(d => d.y).filter(v => typeof v === 'number' && !isNaN(v))
  if (ys.length === 0) {
    return <div className="h-[120px] flex items-center justify-center text-gray-400 text-sm">No data to summarize</div>
  }
  const min = Math.min(...ys)
  const max = Math.max(...ys)
  const avg = ys.reduce((a, b) => a + b, 0) / ys.length
  const latest = ys[ys.length - 1]
  const fmt = (n) => (typeof n === 'number' ? n.toFixed(2) : n)
  const stats = [
    ['Minimum', fmt(min)],
    ['Maximum', fmt(max)],
    ['Average', fmt(avg)],
    ['Latest', fmt(latest)],
    ['Count', ys.length],
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 py-2">
      {stats.map(([label, val]) => (
        <div key={label} className="bg-gray-50 rounded p-3 text-center">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
          <div className="text-base font-bold text-[#4D4D4D] mt-1">{val}{label !== 'Count' && unit ? ` ${unit}` : ''}</div>
        </div>
      ))}
    </div>
  )
}
